/**
 * Profile Theme Design Tokens
 *
 * 10개 테마의 CSS custom property 값을 TypeScript 객체로 관리.
 * PublicProfile만 이 모듈을 import하여 CSS vars로 주입한다.
 */
import { contrastRatio, hexToRgb } from '@/lib/wcag-contrast'

export interface ProfileThemeTokens {
  '--page-bg': string
  '--card-bg': string
  '--card-border': string
  '--card-hover': string
  '--text-primary': string
  '--text-secondary': string
  '--text-tertiary': string
  '--avatar-bg': string
  '--avatar-text': string
  '--avatar-border': string
  '--accent': string
  '--divider': string
  '--footer-text': string
  '--card-shadow': string
  '--card-radius': string
  '--avatar-radius': string
  '--success-bg'?: string
  '--success-text'?: string
  '--error-bg'?: string
  '--error-text'?: string
  [key: string]: string | undefined
}

export type ProfileThemeName =
  | 'clean' | 'dark' | 'glass' | 'aurora' | 'corporate'
  | 'bento' | 'minimal' | 'ocean' | 'neubrutalism' | 'nature'

export interface ThemeConfig {
  button_variant?: string
  button_radius_preset?: string
  button_shadow?: string
  bg_color?: string | null
  text_color?: string | null
  title_color?: string | null
  accent_color?: string | null
  button_color?: string | null
  button_text_color?: string | null
  bg_gradient?: string | null
  bg_image_url?: string | null
  wallpaper_style?: string | null
  wallpaper_pattern?: string | null
  card_bg_color?: string | null
  card_opacity?: number | null
  font_heading?: string | null
  font_body?: string | null
  hide_branding?: boolean
  social_icon_style?: string | null
  header_layout?: string | null
  title_style?: string | null
  title_size?: string | null
  title_font_override?: boolean
  badge_color?: string | null
}

export const profileThemes: Record<ProfileThemeName, ProfileThemeTokens> = {
  clean: {
    '--page-bg': '#FFFFFF',
    '--card-bg': '#F8FAFC',
    '--card-border': '#E2E8F0',
    '--card-hover': '#F1F5F9',
    '--text-primary': '#1E293B',
    '--text-secondary': '#64748B',
    '--text-tertiary': '#94A3B8',
    '--avatar-bg': '#3182F6',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': 'transparent',
    '--accent': '#3182F6',
    '--divider': '#E2E8F0',
    '--footer-text': '#94A3B8',
    '--card-shadow': '0 1px 3px rgba(0,0,0,0.04)',
    '--card-radius': '12px',
    '--avatar-radius': '9999px',
  },
  dark: {
    '--page-bg': '#0F0F0F',
    '--card-bg': '#1A1A1A',
    '--card-border': '#2A2A2A',
    '--card-hover': '#222222',
    '--text-primary': '#F5F5F5',
    '--text-secondary': '#888888',
    '--text-tertiary': '#555555',
    '--avatar-bg': '#3182F6',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': '#2A2A2A',
    '--accent': '#3182F6',
    '--divider': '#2A2A2A',
    '--footer-text': '#555555',
    '--card-shadow': '0 1px 3px rgba(0,0,0,0.3)',
    '--card-radius': '12px',
    '--avatar-radius': '9999px',
    '--success-bg': 'rgba(16, 185, 129, 0.2)',
    '--success-text': '#6EE7B7',
    '--error-bg': 'rgba(244, 63, 94, 0.2)',
    '--error-text': '#FDA4AF',
  },
  glass: {
    '--page-bg': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    '--card-bg': 'rgba(255,255,255,0.15)',
    '--card-border': 'rgba(255,255,255,0.2)',
    '--card-hover': 'rgba(255,255,255,0.22)',
    '--text-primary': '#FFFFFF',
    '--text-secondary': 'rgba(255,255,255,0.7)',
    '--text-tertiary': 'rgba(255,255,255,0.5)',
    '--avatar-bg': 'rgba(255,255,255,0.2)',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': 'rgba(255,255,255,0.3)',
    '--accent': '#FFFFFF',
    '--divider': 'rgba(255,255,255,0.15)',
    '--footer-text': 'rgba(255,255,255,0.4)',
    '--card-shadow': '0 8px 32px rgba(0,0,0,0.1)',
    '--card-radius': '16px',
    '--avatar-radius': '9999px',
    '--success-bg': 'rgba(16, 185, 129, 0.2)',
    '--success-text': '#6EE7B7',
    '--error-bg': 'rgba(244, 63, 94, 0.2)',
    '--error-text': '#FDA4AF',
  },
  aurora: {
    '--page-bg': '#0f0c29',
    '--card-bg': 'rgba(255,255,255,0.08)',
    '--card-border': 'rgba(255,255,255,0.1)',
    '--card-hover': 'rgba(255,255,255,0.14)',
    '--text-primary': '#FFFFFF',
    '--text-secondary': 'rgba(255,255,255,0.6)',
    '--text-tertiary': 'rgba(255,255,255,0.4)',
    '--avatar-bg': 'rgba(78,205,196,0.3)',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': 'rgba(78,205,196,0.5)',
    '--accent': '#4ECDC4',
    '--divider': 'rgba(255,255,255,0.08)',
    '--footer-text': 'rgba(255,255,255,0.3)',
    '--card-shadow': '0 4px 24px rgba(0,0,0,0.2)',
    '--card-radius': '16px',
    '--avatar-radius': '9999px',
    '--success-bg': 'rgba(16, 185, 129, 0.2)',
    '--success-text': '#6EE7B7',
    '--error-bg': 'rgba(244, 63, 94, 0.2)',
    '--error-text': '#FDA4AF',
  },
  corporate: {
    '--page-bg': '#F8F9FA',
    '--card-bg': '#FFFFFF',
    '--card-border': 'transparent',
    '--card-hover': '#F8F9FA',
    '--text-primary': '#212529',
    '--text-secondary': '#6C757D',
    '--text-tertiary': '#ADB5BD',
    '--avatar-bg': '#1B2A4A',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': 'transparent',
    '--accent': '#1B2A4A',
    '--divider': '#E9ECEF',
    '--footer-text': '#ADB5BD',
    '--card-shadow': '0 1px 3px rgba(0,0,0,0.08)',
    '--card-radius': '8px',
    '--avatar-radius': '9999px',
  },
  bento: {
    '--page-bg': '#FAFAFA',
    '--card-bg': '#FFFFFF',
    '--card-border': 'transparent',
    '--card-hover': '#F5F5F5',
    '--text-primary': '#1A1A1A',
    '--text-secondary': '#666666',
    '--text-tertiary': '#999999',
    '--avatar-bg': '#3182F6',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': 'transparent',
    '--accent': '#3182F6',
    '--divider': '#EEEEEE',
    '--footer-text': '#999999',
    '--card-shadow': '0 2px 8px rgba(0,0,0,0.06)',
    '--card-radius': '20px',
    '--avatar-radius': '16px',
  },
  minimal: {
    '--page-bg': '#FFFFFF',
    '--card-bg': 'transparent',
    '--card-border': 'transparent',
    '--card-hover': 'transparent',
    '--text-primary': '#000000',
    '--text-secondary': '#999999',
    '--text-tertiary': '#CCCCCC',
    '--avatar-bg': '#E5E5E5',
    '--avatar-text': '#666666',
    '--avatar-border': 'transparent',
    '--accent': '#000000',
    '--divider': '#EEEEEE',
    '--footer-text': '#CCCCCC',
    '--card-shadow': 'none',
    '--card-radius': '0',
    '--avatar-radius': '9999px',
  },
  ocean: {
    '--page-bg': 'linear-gradient(180deg, #0077B6 0%, #023E8A 50%, #03045E 100%)',
    '--card-bg': 'rgba(255,255,255,0.12)',
    '--card-border': 'rgba(255,255,255,0.15)',
    '--card-hover': 'rgba(255,255,255,0.2)',
    '--text-primary': '#FFFFFF',
    '--text-secondary': 'rgba(255,255,255,0.7)',
    '--text-tertiary': 'rgba(255,255,255,0.5)',
    '--avatar-bg': 'rgba(255,255,255,0.15)',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': '#FFFFFF',
    '--accent': '#90E0EF',
    '--divider': 'rgba(255,255,255,0.1)',
    '--footer-text': 'rgba(255,255,255,0.4)',
    '--card-shadow': '0 4px 16px rgba(0,0,0,0.15)',
    '--card-radius': '9999px',
    '--avatar-radius': '9999px',
    '--success-bg': 'rgba(16, 185, 129, 0.2)',
    '--success-text': '#6EE7B7',
    '--error-bg': 'rgba(244, 63, 94, 0.2)',
    '--error-text': '#FDA4AF',
  },
  neubrutalism: {
    '--page-bg': '#FFFBEB',
    '--card-bg': '#FEF08A',
    '--card-border': '#000000',
    '--card-hover': '#FEF08A',
    '--text-primary': '#000000',
    '--text-secondary': '#333333',
    '--text-tertiary': '#555555',
    '--avatar-bg': '#FEF08A',
    '--avatar-text': '#000000',
    '--avatar-border': '#000000',
    '--accent': '#000000',
    '--divider': '#000000',
    '--footer-text': '#777777',
    '--card-shadow': '4px 4px 0px #000000',
    '--card-radius': '4px',
    '--avatar-radius': '4px',
  },
  nature: {
    '--page-bg': '#F5F0EB',
    '--card-bg': '#FFFFFF',
    '--card-border': '#D4C5B5',
    '--card-hover': '#FAF7F4',
    '--text-primary': '#2D2016',
    '--text-secondary': '#9A8A7B',
    '--text-tertiary': '#B8A99A',
    '--avatar-bg': '#4A635D',
    '--avatar-text': '#FFFFFF',
    '--avatar-border': 'transparent',
    '--accent': '#C67C5B',
    '--divider': '#E5DDD4',
    '--footer-text': '#B8A99A',
    '--card-shadow': '0 1px 4px rgba(0,0,0,0.05)',
    '--card-radius': '24px',
    '--avatar-radius': '9999px',
  },
}

const FONT_FAMILY_ALIASES: Record<string, string> = {
  IBMPlexSansKR: 'IBM Plex Sans KR',
  InstrumentSerif: 'Instrument Serif',
  JetBrainsMono: 'JetBrains Mono',
  NanumGothic: 'Nanum Gothic',
  NanumMyeongjo: 'Nanum Myeongjo',
  PlayfairDisplay: 'Playfair Display',
  SpaceGrotesk: 'Space Grotesk',
  GowunBatang: 'Gowun Batang',
  GowunDodum: 'Gowun Dodum',
}

/**
 * 폰트 이름의 모양을 강제한다 — 서버 `PageTheme::FONT_NAME_FORMAT` 과 같은 규칙.
 *
 * 🔴 이 값은 아래에서 `"${font}", ...` 로 **CSS 선언에 보간된다.** 따옴표·세미콜론·
 *    중괄호가 든 이름은 선언을 닫고 자기 선언을 덧붙일 수 있다. 서버가 공개 직렬화에서
 *    이미 거르지만(`PageTheme#safe_font_heading`), 여기서도 막는다 — 이 함수는 미리보기·
 *    편집기 등 **서버를 거치지 않은 값**으로도 불린다.
 */
const FONT_NAME_FORMAT = /^[\p{L}\p{N}][\p{L}\p{N} \-_.]{0,63}$/u

function normalizeFontFamily(font?: string | null): string | null {
  if (!font) return null
  const resolved = FONT_FAMILY_ALIASES[font] || font
  // 모양이 어긋나면 버린다 — 빈 값과 같은 상태(스타일시트 기본 폰트)가 된다.
  return FONT_NAME_FORMAT.test(resolved) ? resolved : null
}

function mixedColor(color: string, opacity = 1): string {
  if (opacity >= 1) return color
  return `color-mix(in srgb, ${color} ${Math.round(opacity * 100)}%, transparent)`
}

function rgbToHex(value: { r: number; g: number; b: number }): string {
  const toHex = (channel: number) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0')
  return `#${toHex(value.r)}${toHex(value.g)}${toHex(value.b)}`
}

function mixHexColors(foreground: string, background: string, foregroundWeight = 0.82): string | null {
  const fg = hexToRgb(foreground)
  const bg = hexToRgb(background)
  if (!fg || !bg) return null
  const bgWeight = 1 - foregroundWeight
  return rgbToHex({
    r: fg.r * foregroundWeight + bg.r * bgWeight,
    g: fg.g * foregroundWeight + bg.g * bgWeight,
    b: fg.b * foregroundWeight + bg.b * bgWeight,
  })
}

function readableTextColor(background: string, preferred?: string | null): string {
  if (preferred) return preferred
  const light = '#ffffff'
  const dark = '#0f172a'
  const lightContrast = contrastRatio(light, background) ?? 0
  const darkContrast = contrastRatio(dark, background) ?? 0
  return lightContrast >= darkContrast ? light : dark
}

function readableSubtextColor(text: string, background?: string | null, emphasis = 0.82): string {
  if (!background) return mixedColor(text, emphasis)
  return mixHexColors(text, background, emphasis) || mixedColor(text, emphasis)
}

/**
 * Wallpaper pattern SVGs (currentColor-based, inlined for CSS data URLs).
 * Source: app/frontend/assets/wallpaper-patterns/{grid,morph,organic,matrix}.svg
 * Kept in sync manually — if the source SVGs change, update these too.
 */
const PATTERN_SVGS: Record<string, string> = {
  grid:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" fill="none">' +
    '<path d="M0 30H120M0 60H120M0 90H120M30 0V120M60 0V120M90 0V120" stroke="COLOR" stroke-opacity="0.24" stroke-width="2"/>' +
    '</svg>',
  morph:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" fill="none">' +
    '<path d="M14 24C33 5 64 4 86 18C108 32 111 65 98 85C85 105 58 115 34 104C10 93 -5 61 14 24Z" stroke="COLOR" stroke-opacity="0.22" stroke-width="4"/>' +
    '<path d="M36 38C50 24 72 24 84 36C96 48 95 70 84 84C73 98 49 100 36 88C23 76 22 52 36 38Z" stroke="COLOR" stroke-opacity="0.16" stroke-width="3"/>' +
    '</svg>',
  organic:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" fill="none">' +
    '<path d="M20 74C20 49 34 22 59 22C84 22 102 42 102 66C102 90 82 101 60 101C38 101 20 96 20 74Z" stroke="COLOR" stroke-opacity="0.22" stroke-width="4"/>' +
    '<path d="M18 32C24 22 36 18 46 24C56 30 59 44 52 54C45 64 31 67 22 59C13 51 12 42 18 32Z" fill="COLOR" fill-opacity="0.12"/>' +
    '</svg>',
  matrix:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" fill="none">' +
    '<circle cx="20" cy="20" r="4" fill="COLOR" fill-opacity="0.28"/>' +
    '<circle cx="60" cy="20" r="4" fill="COLOR" fill-opacity="0.18"/>' +
    '<circle cx="100" cy="20" r="4" fill="COLOR" fill-opacity="0.28"/>' +
    '<circle cx="20" cy="60" r="4" fill="COLOR" fill-opacity="0.18"/>' +
    '<circle cx="60" cy="60" r="4" fill="COLOR" fill-opacity="0.32"/>' +
    '<circle cx="100" cy="60" r="4" fill="COLOR" fill-opacity="0.18"/>' +
    '<circle cx="20" cy="100" r="4" fill="COLOR" fill-opacity="0.28"/>' +
    '<circle cx="60" cy="100" r="4" fill="COLOR" fill-opacity="0.18"/>' +
    '<circle cx="100" cy="100" r="4" fill="COLOR" fill-opacity="0.28"/>' +
    '<path d="M20 20H60V60H20zM60 60H100V100H60z" stroke="COLOR" stroke-opacity="0.14" stroke-width="2"/>' +
    '</svg>',
}

export function patternDataUrl(pattern: string, color: string): string {
  const svg = PATTERN_SVGS[pattern]
  if (!svg) return ''
  // Use split/join instead of replaceAll to keep the tsconfig target flexible —
  // the wider tsconfig still targets es2020 which lacks String.prototype.replaceAll.
  const filled = svg.split('COLOR').join(color)
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(filled)}")`
}

// ━━━ THEME RESOLUTION — 3-TIER PIPELINE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//
// User input (ThemeConfig) flows through three explicit tiers before
// landing as CSS custom properties on PublicProfile:
//
//   Tier 1  Palette          raw colors the user picked, with named-theme
//                            defaults filled in. NO derivation logic.
//
//   Tier 2  Semantic         role-based tokens derived from the palette
//                            (text-primary/secondary/tertiary, surface,
//                            border, button surface). Variant rules
//                            (solid/glass/outline) apply HERE so they
//                            transform a single semantic layer rather
//                            than scattering through the resolver.
//
//   Tier 3  Component        the actual --page-bg / --card-bg / etc. CSS
//                            vars consumed by templates. This is the only
//                            tier that knows the real var names — rename
//                            a var by editing one place.
//
// Wallpaper, typography, and button-shape rules apply orthogonally on top
// of the color stack (they don't read from the semantic tier).
//
// Why three tiers? Before this refactor, resolveTheme was a 100-line
// imperative function with hidden surprises like "card_bg_color silently
// rewrites --card-border to a 12% accent tint". Splitting the work makes
// each surprise easy to find — and remove. See the borderCard branch in
// deriveSemantic() for the documented removal of that specific surprise.

type ResolvedPalette = {
  page: string
  text: string
  title: string
  accent: string
  card: string
  cardOpacity: number
  button: string | null
  buttonText: string | null
  variant: string
}

function extractPalette(
  baseTokens: ProfileThemeTokens,
  config: ThemeConfig,
): ResolvedPalette {
  return {
    page: config.bg_color || baseTokens['--page-bg'] || '#ffffff',
    text: config.text_color || baseTokens['--text-primary'] || '#0f172a',
    title:
      config.title_color || config.text_color || baseTokens['--text-primary'] || '#0f172a',
    accent: config.accent_color || baseTokens['--accent'] || '#2563eb',
    card: config.card_bg_color || baseTokens['--card-bg'] || '#ffffff',
    cardOpacity: Number(config.card_opacity ?? 0.88),
    button: config.button_color ?? null,
    buttonText: config.button_text_color ?? null,
    variant: config.button_variant || 'solid',
  }
}

type Semantic = {
  textPrimary: string
  textSecondary: string
  textTertiary: string
  surfaceCard: string
  surfaceCardHover: string
  borderCard: string | null
  // Button surface tokens are NULLABLE on purpose. When the user hasn't
  // picked a button_color and the active variant is the default 'solid',
  // we leave these unset and let ProfileLinkCard's CSS fall back to the
  // card surface (background: var(--button-bg, var(--card-bg))). That's
  // what makes the Clean theme show clean white link rows instead of
  // a synthesized accent-blue fill. Glass/outline variants always need
  // a value because they imply a deliberate visual treatment.
  buttonBg: string | null
  buttonText: string | null
  buttonSubtext: string | null
  buttonBorder: string | null
  buttonBackdrop: string | null
}

function deriveSemantic(p: ResolvedPalette, baseBorderCard: string | null): Semantic {
  // Text scale via opacity-mixing — single source of truth for the
  // primary/secondary/tertiary cascade.
  const textPrimary = p.text
  const textSecondary = mixedColor(p.text, 0.72)
  const textTertiary = mixedColor(p.text, 0.5)

  // Card surface translucency.
  const surfaceCard = mixedColor(p.card, p.cardOpacity)
  const surfaceCardHover = mixedColor(p.card, Math.min(p.cardOpacity + 0.08, 1))

  // Card border — RESPECT the base theme default. Previously this branch
  // overwrote the border with a 12% accent tint whenever card_bg_color was
  // set, which silently destroyed the user's intent. Removed in the 3-tier
  // refactor: each base theme already declares a border that fits its
  // design language, and the user can override card_bg_color independently.
  const borderCard = baseBorderCard

  // Button surface — variant decides whether the user-picked color sits as
  // a solid fill, a frosted glass overlay, or just an outline. For solid
  // variant we DO NOT synthesize a button color from accent: an unset
  // button_color means "inherit the card surface" so themes like Clean
  // render as the user expects.
  let buttonBg: string | null = null
  let buttonText: string | null = null
  let buttonBorder: string | null = null
  let buttonBackdrop: string | null = null

  if (p.variant === 'glass') {
    const base = p.button || p.accent || p.text || '#ffffff'
    buttonBg = mixedColor(base, 0.18)
    buttonBorder = mixedColor(p.buttonText || p.accent || p.text || '#ffffff', 0.24)
    buttonText = p.buttonText || '#ffffff'
    buttonBackdrop = 'blur(14px)'
  } else if (p.variant === 'outline') {
    const tint = p.accent || p.text || '#0f172a'
    buttonBg = 'transparent'
    buttonBorder = tint
    buttonText = tint
    buttonBackdrop = 'none'
  } else if (p.button) {
    // solid + explicit user button color
    buttonBg = p.button
    buttonText = readableTextColor(p.button, p.buttonText || null)
    buttonBorder = mixedColor(p.button, 0.88)
    buttonBackdrop = 'none'
  } else if (p.buttonText) {
    // solid + only buttonText set (rare): respect it without forcing a bg
    buttonText = p.buttonText
    buttonBackdrop = 'none'
  } else {
    // solid + no overrides → leave nullable so card surface bleeds through
    buttonBackdrop = 'none'
  }

  const buttonSubtext =
    buttonText && buttonBg && buttonBg !== 'transparent'
      ? readableSubtextColor(buttonText, buttonBg, 0.84)
      : buttonText
        ? mixedColor(buttonText, 0.84)
        : null

  return {
    textPrimary,
    textSecondary,
    textTertiary,
    surfaceCard,
    surfaceCardHover,
    borderCard,
    buttonBg,
    buttonText,
    buttonSubtext,
    buttonBorder,
    buttonBackdrop,
  }
}

function semanticToComponent(
  base: Record<string, string>,
  s: Semantic,
  p: ResolvedPalette,
): Record<string, string> {
  const out: Record<string, string> = { ...base }
  out['--text-primary'] = s.textPrimary
  out['--text-secondary'] = s.textSecondary
  out['--text-tertiary'] = s.textTertiary
  out['--accent'] = p.accent
  out['--avatar-bg'] = p.accent
  out['--card-bg'] = s.surfaceCard
  out['--card-hover'] = s.surfaceCardHover
  if (s.borderCard != null) out['--card-border'] = s.borderCard
  // Button vars are only written when the semantic tier produced a value.
  // Otherwise the CSS fallback chain in ProfileLinkCard takes over —
  // see the comment on Semantic.buttonBg.
  if (s.buttonBg != null) out['--button-bg'] = s.buttonBg
  if (s.buttonText != null) {
    out['--button-text'] = s.buttonText
    out['--avatar-text'] = s.buttonText
  }
  if (s.buttonSubtext != null) out['--button-subtext'] = s.buttonSubtext
  if (s.buttonBorder != null) out['--button-border'] = s.buttonBorder
  if (s.buttonBackdrop != null) out['--button-backdrop'] = s.buttonBackdrop
  out['--title-color'] = p.title
  return out
}

// ━━━ ORTHOGONAL LAYERS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// These mutate the component-tier tokens but don't read from the semantic
// tier — they're independent concerns layered on top of the color stack.

function applyWallpaper(tokens: Record<string, string>, config: ThemeConfig): void {
  const style = config.wallpaper_style || deriveWallpaperStyle(config)

  if (style === 'image' && config.bg_image_url) {
    // Image wallpaper scrim — strong enough that white text stays readable
    // on bright photos (latte, beach, fashion editorial, etc.). WCAG-driven.
    tokens['--page-bg'] = `linear-gradient(rgba(15, 23, 42, 0.50), rgba(15, 23, 42, 0.30)), url(${config.bg_image_url}) center / cover no-repeat`
  } else if (style === 'gradient' && config.bg_gradient) {
    tokens['--page-bg'] = config.bg_gradient
  } else if (config.bg_color) {
    tokens['--page-bg'] = config.bg_color
  }

  // Pattern overlay — repeating SVG rendered behind content via the
  // PublicProfile .wallpaper-layer element.
  if (style === 'pattern' && config.wallpaper_pattern) {
    const patternColor = config.accent_color || config.text_color || '#0f172a'
    const url = patternDataUrl(config.wallpaper_pattern, patternColor)
    if (url) {
      tokens['--wallpaper-pattern-image'] = url
      tokens['--wallpaper-pattern-size'] = '120px 120px'
    }
  } else {
    tokens['--wallpaper-pattern-image'] = 'none'
  }

  tokens['--wallpaper-blur'] = style === 'blur' ? '22px' : '0px'
}

function applyTypography(tokens: Record<string, string>, config: ThemeConfig): void {
  tokens['--title-size'] = config.title_size === 'large' ? '28px' : '22px'
  tokens['--title-letter-spacing'] = config.title_size === 'large' ? '-0.04em' : '-0.02em'
  const headFont = normalizeFontFamily(config.font_heading)
  const bodyFont = normalizeFontFamily(config.font_body)
  if (headFont) tokens['--font-heading'] = `"${headFont}", "Pretendard", sans-serif`
  if (bodyFont) tokens['--font-body'] = `"${bodyFont}", "Pretendard", sans-serif`
}

function applyButtonShape(tokens: Record<string, string>, config: ThemeConfig): void {
  tokens['--card-radius'] = radiusValue(config.button_radius_preset || 'rounder')
  tokens['--button-shadow'] = shadowValue(config.button_shadow || 'none')
  // Glass variant gets a softer card shadow to reinforce the frosted feel.
  if ((config.button_variant || 'solid') === 'glass') {
    tokens['--card-shadow'] = '0 10px 30px rgba(15,23,42,0.12)'
  }
}

export function resolveTheme(
  base: ProfileThemeName,
  config: ThemeConfig,
): Record<string, string> {
  const baseTokens = profileThemes[base] || profileThemes.clean

  // Materialize the base theme as a plain Record so the orthogonal layers
  // can mutate freely without touching the immutable profileThemes table.
  const baseRecord: Record<string, string> = {}
  for (const [k, v] of Object.entries(baseTokens)) {
    if (v != null) baseRecord[k] = v
  }

  const palette = extractPalette(baseTokens, config)
  const semantic = deriveSemantic(palette, baseTokens['--card-border'] || null)
  const tokens = semanticToComponent(baseRecord, semantic, palette)

  applyWallpaper(tokens, config)
  applyTypography(tokens, config)
  applyButtonShape(tokens, config)

  return tokens
}


function deriveWallpaperStyle(config: ThemeConfig): string {
  if (config.wallpaper_style) return config.wallpaper_style
  if (config.bg_image_url) return 'image'
  if (config.bg_gradient) return 'gradient'
  return 'fill'
}

function radiusValue(preset?: string): string {
  const map: Record<string, string> = {
    square: '0px',
    round: '8px',
    rounder: '16px',
    full: '9999px',
  }
  return map[preset || 'rounder'] || '16px'
}

function shadowValue(shadow?: string): string {
  const map: Record<string, string> = {
    none: 'none',
    soft: '0 10px 24px rgba(15,23,42,0.10)',
    strong: '0 16px 40px rgba(15,23,42,0.18)',
    hard: '4px 4px 0 rgba(15,23,42,0.85)',
  }
  return map[shadow || 'none'] || 'none'
}

export function toStyleVars(tokens: Record<string, string>): string {
  return Object.entries(tokens)
    .map(([k, v]) => `${k}:${v}`)
    .join(';')
}

export type LinkLayout = 'default' | 'bento' | 'brutal' | 'nature' | 'minimal' | 'corporate' | 'ocean'

export function deriveLinkLayout(theme: ProfileThemeName): LinkLayout {
  if (theme === 'bento') return 'bento'
  if (theme === 'neubrutalism') return 'brutal'
  if (theme === 'nature') return 'nature'
  if (theme === 'minimal') return 'minimal'
  if (theme === 'corporate') return 'corporate'
  if (theme === 'ocean') return 'ocean'
  return 'default'
}

export const BENTO_ACCENTS = ['#3182F6', '#FF6B6B', '#4ECDC4', '#FFD93D', '#6C5CE7', '#FF8A5C']
export const BRUTAL_COLORS = ['#FEF08A', '#BBF7D0', '#FBCFE8', '#BAE6FD', '#FDE68A']
